import { useEffect, useRef, useCallback } from 'react';
import { SNAP_THRESHOLD_PX } from './lineDrawingConstants';

interface PolygonCompleteData {
  coordinates: Array<{ lat: number; lng: number }>;
}

interface UsePolygonDrawingParams {
  map: google.maps.Map | null;
  isActive: boolean;
  fillColor: string;
  snapTargets: Array<{ lat: number; lng: number }>;
  onPolygonComplete: (data: PolygonCompleteData) => void;
  snapEnabled?: boolean;
}

/**
 * Manual polygon drawing on a Google Map.
 *
 * Replaces google.maps.drawing.DrawingManager, which was removed from the
 * Maps JavaScript API in v3.65. Click to place vertices; click the first
 * vertex, double-click, or right-click to close the polygon; Esc cancels.
 */
export function usePolygonDrawing({
  map,
  isActive,
  fillColor,
  snapTargets,
  onPolygonComplete,
  snapEnabled = true,
}: UsePolygonDrawingParams) {
  const currentPointsRef = useRef<google.maps.LatLng[]>([]);
  const previewPolygonRef = useRef<google.maps.Polygon | null>(null);
  const vertexMarkersRef = useRef<google.maps.Marker[]>([]);
  const snapMarkerRef = useRef<google.maps.Marker | null>(null);
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const moveListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const dblClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const rightClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const keyListenerRef = useRef<((e: KeyboardEvent) => void) | null>(null);

  const fillColorRef = useRef(fillColor);
  const onPolygonCompleteRef = useRef(onPolygonComplete);
  const snapEnabledRef = useRef(snapEnabled);
  const snapTargetsRef = useRef(snapTargets);
  fillColorRef.current = fillColor;
  onPolygonCompleteRef.current = onPolygonComplete;
  snapEnabledRef.current = snapEnabled;
  snapTargetsRef.current = snapTargets;

  const pixelDistance = useCallback(
    (a: google.maps.LatLng, b: google.maps.LatLng): number => {
      if (!map) return Infinity;
      const projection = map.getProjection();
      if (!projection) return Infinity;

      const zoom = map.getZoom() || 20;
      const scale = Math.pow(2, zoom);

      const pa = projection.fromLatLngToPoint(a);
      const pb = projection.fromLatLngToPoint(b);
      if (!pa || !pb) return Infinity;

      const dx = (pa.x - pb.x) * scale;
      const dy = (pa.y - pb.y) * scale;
      return Math.sqrt(dx * dx + dy * dy);
    },
    [map]
  );

  const findSnapTarget = useCallback(
    (latLng: google.maps.LatLng): google.maps.LatLng | null => {
      if (!snapEnabledRef.current) return null;

      let closestTarget: google.maps.LatLng | null = null;
      let closestDistPx = SNAP_THRESHOLD_PX;

      for (const target of snapTargetsRef.current) {
        const targetLatLng = new google.maps.LatLng(target.lat, target.lng);
        const distPx = pixelDistance(latLng, targetLatLng);
        if (distPx < closestDistPx) {
          closestDistPx = distPx;
          closestTarget = targetLatLng;
        }
      }

      return closestTarget;
    },
    [pixelDistance]
  );

  const clearWorkingState = useCallback(() => {
    currentPointsRef.current = [];

    if (previewPolygonRef.current) {
      previewPolygonRef.current.setMap(null);
      previewPolygonRef.current = null;
    }
    vertexMarkersRef.current.forEach((m) => m.setMap(null));
    vertexMarkersRef.current = [];
    if (snapMarkerRef.current) {
      snapMarkerRef.current.setMap(null);
      snapMarkerRef.current = null;
    }
  }, []);

  const finalizePolygon = useCallback(() => {
    const points = currentPointsRef.current;
    if (points.length < 3) {
      clearWorkingState();
      return;
    }

    const coordinates = points.map((p) => ({ lat: p.lat(), lng: p.lng() }));
    onPolygonCompleteRef.current({ coordinates });
    clearWorkingState();
  }, [clearWorkingState]);

  const updatePreview = useCallback(
    (cursorPos: google.maps.LatLng | null) => {
      if (!map) return;

      const path = cursorPos
        ? [...currentPointsRef.current, cursorPos]
        : [...currentPointsRef.current];

      if (path.length === 0) return;

      if (!previewPolygonRef.current) {
        previewPolygonRef.current = new google.maps.Polygon({
          paths: path,
          fillColor: fillColorRef.current,
          fillOpacity: 0.2,
          strokeColor: fillColorRef.current,
          strokeWeight: 2,
          map,
          clickable: false,
        });
      } else {
        previewPolygonRef.current.setPath(path);
      }
    },
    [map]
  );

  // Attach / detach map listeners
  useEffect(() => {
    if (!map || !isActive) {
      clearWorkingState();
      clickListenerRef.current?.remove();
      moveListenerRef.current?.remove();
      dblClickListenerRef.current?.remove();
      rightClickListenerRef.current?.remove();
      if (keyListenerRef.current) {
        document.removeEventListener('keydown', keyListenerRef.current);
        keyListenerRef.current = null;
      }
      if (map) {
        map.setOptions({ draggableCursor: '' });
      }
      return;
    }

    map.setOptions({ draggableCursor: 'crosshair' });

    // Click handler: add vertex, or close the polygon when clicking the first vertex
    clickListenerRef.current = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;

      const points = currentPointsRef.current;

      // Clicking near the first vertex closes the polygon
      if (
        points.length >= 3 &&
        pixelDistance(e.latLng, points[0]) < SNAP_THRESHOLD_PX
      ) {
        finalizePolygon();
        return;
      }

      const snapTarget = findSnapTarget(e.latLng);
      const point = snapTarget || e.latLng;
      points.push(point);

      const marker = new google.maps.Marker({
        position: point,
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: '#FFFFFF',
          fillOpacity: 1,
          strokeColor: fillColorRef.current,
          strokeWeight: 2,
        },
        clickable: false,
        zIndex: 998,
      });
      vertexMarkersRef.current.push(marker);

      updatePreview(null);
    });

    // Mousemove handler: live preview + snap indicator
    moveListenerRef.current = map.addListener(
      'mousemove',
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;

        const snapTarget = findSnapTarget(e.latLng);
        const cursorPos = snapTarget || e.latLng;

        if (snapTarget) {
          if (!snapMarkerRef.current) {
            snapMarkerRef.current = new google.maps.Marker({
              position: snapTarget,
              map,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#FFFFFF',
                fillOpacity: 0.8,
                strokeColor: fillColorRef.current,
                strokeWeight: 2,
              },
              clickable: false,
              zIndex: 999,
            });
          } else {
            snapMarkerRef.current.setPosition(snapTarget);
            snapMarkerRef.current.setMap(map);
          }
        } else if (snapMarkerRef.current) {
          snapMarkerRef.current.setMap(null);
        }

        if (currentPointsRef.current.length > 0) {
          updatePreview(cursorPos);
        }
      }
    );

    // Double-click handler: close polygon
    dblClickListenerRef.current = map.addListener('dblclick', (e: google.maps.MapMouseEvent) => {
      e.stop?.();
      // The click event fires before dblclick, so the duplicate last vertex
      // from the single-click needs to be removed
      if (currentPointsRef.current.length > 3) {
        currentPointsRef.current.pop();
        const marker = vertexMarkersRef.current.pop();
        marker?.setMap(null);
      }
      finalizePolygon();
    });

    // Right-click handler: close polygon
    rightClickListenerRef.current = map.addListener('rightclick', () => {
      finalizePolygon();
    });

    // Escape cancels the in-progress polygon
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearWorkingState();
      }
    };
    keyListenerRef.current = keyHandler;
    document.addEventListener('keydown', keyHandler);

    return () => {
      clickListenerRef.current?.remove();
      moveListenerRef.current?.remove();
      dblClickListenerRef.current?.remove();
      rightClickListenerRef.current?.remove();
      if (keyListenerRef.current) {
        document.removeEventListener('keydown', keyListenerRef.current);
        keyListenerRef.current = null;
      }
      map.setOptions({ draggableCursor: '' });
    };
  }, [
    map,
    isActive,
    pixelDistance,
    findSnapTarget,
    finalizePolygon,
    clearWorkingState,
    updatePreview,
  ]);

  return {
    cancelCurrentPolygon: clearWorkingState,
    hasPolygonInProgress: () => currentPointsRef.current.length > 0,
  };
}
